export class EventBase {
  /* ================================================================
     Component State Flags (Updated for EventComponent compatibility)
  ===================================================================*/
  protected isAttendeeCountRequired = false;
  protected isWaitlistEnabled = false;
  protected isMaxBookingsPerSlot = false;
  protected isChargable = false;
  protected isHoldBooking = false;
  protected isMaxBookingPerMembership = false;
  protected isMaxBookingPerPerson = false;


  // Event-specific: Always disabled for events
  protected isPeakTimeEnabled = false;
  protected isBreakTimeEnabled = false;


  protected isAllowForceBooking = false;
  protected isReadOnly = false;
  protected editStatus = false;
  protected viewStatus = false;
  protected addEventStatus = true;


  /* ================================================================
     Properties for EventComponent Compatibility
  ===================================================================*/
  protected facilityDateModel: Date = null;
  protected minDate: Date = new Date();
  protected facility: any = null;
  protected iconImage: any[] = [];
  protected viewIcon = { name: '', iconClass: '' };
  protected height: string = '';
  protected scrollOptions: any = {};
  protected uiSetting: any = {};
  protected eventValidationObj: any = null;


  // UI state properties
  protected shouldSlide = false;
  protected popupTitle = 'Error';
  protected popUpMessage: any = '';
  protected pageName = 'Create Event';
  protected clubName = '';


  // Arrays and objects
  protected facility_list: any[] = [];
  protected facilityArray: any[] = [];
  protected tempFacilityId = '';
  protected redirectConfirmationMessage = '';


  // Flags
  protected saveFlag = false;
  protected cancleButton = false;
  protected deleteMessage = false;
  protected cancleFlag = false;
  protected functionHallFlag = false;
  protected amenitiesFlag = false;
  protected defaultValue = false;


  // Vendor/category state
  protected currentCategory: any = {};
  protected vendor: any = null;
  protected unitNames = '';


  /* ================================================================
     Timing Variables
  ===================================================================*/
  protected closeTimeArray = [];
  protected openTime_hour = '';
  protected openTime_minute = '';
  protected closeTime_hour = '';
  protected closeTime_minute = '';
  protected peekTimingsArray = [];
  protected closedTime = {};
  protected closedStartTime_hour = '';
  protected closedStartTime_minute = '';
  protected closedStartTime = '';
  protected closedEndTime = '';
  protected closedEndTime_hour = '';
  protected closedEndTime_minute = '';
  protected peekStartTime = '';
  protected peekStartTime_hour = '';
  protected peekStartTime_minute = '';
  protected peekEndTime = '';
  protected peekEndTime_hour = '';
  protected peekEndTime_minute = '';
  protected peakTime = {};
  protected bookingUnit_hour = '';
  protected bookingUnit_minute = '';


  /* ================================================================
     System Flags
  ===================================================================*/
  protected isGSTApplicable = false;
  protected isCourtSelection = false;
  protected isOpenBookingOnFriday = false;
  protected isOpenNextMonthBooking = false;
  protected isSlotsLocked = true; // Always true for events
  protected isCreatedForGroup = false;
  protected isWeeklyRepeatBooking = false;


  constructor() {
    // Set minimum date to today
    this.minDate.setHours(0, 0, 0, 0);
  }


  /* ================================================================
     Toggle Methods for UI Controls (Fixed signatures)
  ===================================================================*/


  /**
   * Toggle buttons for active or not (Updated for EventComponent compatibility)
   * @param that Component instance
   * @param toggleType Toggle case number
   * @param value Boolean state to set
   */
  toggle(that: any, toggleType: number, value: boolean): void {
    switch (toggleType) {
      case 1:
        that.isAttendeeCountRequired = value;
        that.facility.isAttendeeCountRequired = value;
        if (!value) {
          that.facility.maxNumberOfPeople = '';
        }
        break;
      case 2:
        that.isWaitlistEnabled = value;
        that.facility.isWaitlistEnabled = value;
        if (!value) {
          that.facility.waitlistCount = '';
        }
        break;
      case 3:
        that.isChargable = value;
        that.facility.isChargable = value;
        break;
      case 4:
        that.isHoldBooking = value;
        that.facility.isHoldBooking = value;
        break;
      case 5:
        that.isMaxBookingPerMembership = value;
        that.facility.isMaxBookingPerMembership = value;
        break;
      case 6:
        that.isMaxBookingPerPerson = value;
        that.facility.isMaxBookingPerPerson = value;
        break;
      case 7:
        // Event-specific: Peak time always disabled
        that.isPeakTimeEnabled = false;
        that.facility.isPeakTimeEnabled = false;
        break;
      case 8:
        // Event-specific: Break time always disabled
        that.isBreakTimeEnabled = false;
        that.facility.isBreakTimeEnabled = false;
        break;
      case 9:
        that.isMaxBookingsPerSlot = value;
        that.facility.isMaxBookingsPerSlot = value;
        if (!value) {
          that.facility.maxBookingsPerSlot = '';
        }
        break;
      case 10:
        that.isAllowForceBooking = value;
        that.facility.isAllowForceBooking = value;
        break;
      case 11:
        that.isCourtSelection = value;
        that.facility.isCourtSelection = value;
        break;
      case 12:
        that.isOpenBookingOnFriday = value;
        that.facility.isOpenBookingOnFriday = value;
        break;
      case 13:
        // Event-specific: Slots always locked
        that.isSlotsLocked = true;
        that.facility.isSlotsLocked = true;
        break;
      case 14:
        that.isOpenNextMonthBooking = value;
        that.facility.isOpenNextMonthBooking = value;
        break;
      case 15:
        that.isCreatedForGroup = value;
        that.facility.isCreatedForGroup = value;
        break;
      case 16:
        that.isWeeklyRepeatBooking = value;
        that.facility.isWeeklyRepeatBooking = value;
        break;
      case 17:
        that.isGSTApplicable = value;
        that.facility.isGSTApplicable = value;
        if (!value) {
          that.facility.GSTValue = '';
        }
        break;
    }
  }


  /* ================================================================
     Event-Specific Methods (New)
  ===================================================================*/


  /**
   * Handle date change for events (ng2-bootstrap 1.1.16 compatible)
   */
  protected onDateChange(that: any, date: Date): void {
    if (date) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      that.facility.date = `${year}-${month}-${day}`;
      that.facilityDateModel = date;


      // Validate if validation object exists
      if (that.eventValidationObj) {
        that.eventValidationObj.validateFacility(that, 'facility.date', '');
      }
    }
  }


  /**
   * Toggle pricing for 5-tier system
   */
  protected togglePricing(that: any, memberType: string, enabled: boolean): void {
    if (!that.facility.activityCharges) {
      that.facility.activityCharges = {};
    }
    if (!that.facility.activityCharges[memberType]) {
      that.facility.activityCharges[memberType] = { enabled: false, amount: '' };
    }
    that.facility.activityCharges[memberType].enabled = enabled;
    if (!enabled) {
      that.facility.activityCharges[memberType].amount = '';
    }
  }


  /**
   * Set default waitlist count
   */
  protected setDefaultWaitlist(that: any): void {
    if (that.isWaitlistEnabled && (!that.facility.waitlistCount || that.facility.waitlistCount === '')) {
      that.facility.waitlistCount = '3';
    }
  }


  /**
   * Build time strings from hour/minute components (Angular 2.3.1 compatible)
   */
  protected buildTimeStrings(that: any): void {
    if (that.openTime_hour && that.openTime_minute) {
      const hour = that.openTime_hour.length === 1 ? '0' + that.openTime_hour : that.openTime_hour;
      const minute = that.openTime_minute.length === 1 ? '0' + that.openTime_minute : that.openTime_minute;
      that.facility.timings.openTime = `${hour}:${minute}`;
    }


    if (that.closeTime_hour && that.closeTime_minute) {
      const hour = that.closeTime_hour.length === 1 ? '0' + that.closeTime_hour : that.closeTime_hour;
      const minute = that.closeTime_minute.length === 1 ? '0' + that.closeTime_minute : that.closeTime_minute;
      that.facility.timings.closeTime = `${hour}:${minute}`;
    }
  }


  /**
   * Parse time strings to hour/minute components
   */
  protected parseTimeStrings(that: any): void {
    if (that.facility.timings && that.facility.timings.openTime) {
      const openTimeParts = that.facility.timings.openTime.split(':');
      that.openTime_hour = openTimeParts[0];
      that.openTime_minute = openTimeParts[1];
    }


    if (that.facility.timings && that.facility.timings.closeTime) {
      const closeTimeParts = that.facility.timings.closeTime.split(':');
      that.closeTime_hour = closeTimeParts[0];
      that.closeTime_minute = closeTimeParts[1];
    }
  }


  /* ================================================================
     Status Management Methods
  ===================================================================*/


  /**
   * Change status for view Event mode
   * Toggle between view and edit modes
   */
  changeStatus(that: any): void {
    try {
      that.isReadOnly = !that.isReadOnly;
      that.editStatus = !that.editStatus;
      that.pageName = that.editStatus ? 'Edit Event' : 'View Event';
      this.setLocalFlagWithEventRules(that);
      this.setDefaultValues(that);
    } catch (err) {
      console.log(err);
    }
  }


  /**
   * Set local flags with event rules
   * Map facility object properties to component flags
   */
  setLocalFlagWithEventRules(that: any): void {
    that.isAttendeeCountRequired = that.facility.isAttendeeCountRequired;
    that.isWaitlistEnabled = that.facility.isWaitlistEnabled;
    that.isMaxBookingsPerSlot = that.facility.isMaxBookingsPerSlot;
    that.isChargable = that.facility.isChargable;
    that.isAllowForceBooking = that.facility.isAllowForceBooking;
    that.isHoldBooking = that.facility.isHoldBooking;
    that.isMaxBookingPerMembership = that.facility.isMaxBookingPerMembership;
    that.isMaxBookingPerPerson = that.facility.isMaxBookingPerPerson;


    // Event-specific: Always disable peak and break times
    that.isPeakTimeEnabled = false;
    that.isBreakTimeEnabled = false;
    that.facility.isPeakTimeEnabled = false;
    that.facility.isBreakTimeEnabled = false;


    // Legacy flags (kept for compatibility)
    that.isOpenBookingOnFriday = that.facility.isOpenBookingOnFriday;
    that.isOpenNextMonthBooking = that.facility.isOpenNextMonthBooking;
    that.isSlotsLocked = true; // Always true for events
    that.isCreatedForGroup = that.facility.isCreatedForGroup;
    that.isWeeklyRepeatBooking = that.facility.isWeeklyRepeatBooking;
    that.isGSTApplicable = that.facility.isGSTApplicable;
  }


  /* ================================================================
     Default Value Management
  ===================================================================*/


  /**
   * Set Default Values if values are undefined or null
   * Ensures all facility properties have default values
   */
  setDefaultValues(that: any): void {
    try {
      // Event-specific: Single max people field
      if (that.facility.maxNumberOfPeople == null || that.facility.maxNumberOfPeople === undefined) {
        that.facility.maxNumberOfPeople = '';
      }


      // Legacy fields (kept for compatibility)
      if (that.facility.maxNumberOfPeopleWeekend == null || that.facility.maxNumberOfPeopleWeekend === undefined) {
        that.facility.maxNumberOfPeopleWeekend = '';
      }


      if (that.facility.maxNumberOfPeopleWeekday == null || that.facility.maxNumberOfPeopleWeekday === undefined) {
        that.facility.maxNumberOfPeopleWeekday = '';
      }


      if (that.facility.waitlistCount == null || that.facility.waitlistCount === undefined) {
        that.facility.waitlistCount = '';
      }


      if (that.facility.amount && (that.facility.amount.value == null || that.facility.amount.value === undefined)) {
        that.facility.amount.value = '';
      }


      if (that.facility.holdBookingDays == null || that.facility.holdBookingDays === undefined) {
        that.facility.holdBookingDays = '';
      }


      if (that.facility.maxWeekdayBooking == null || that.facility.maxWeekdayBooking === undefined) {
        that.facility.maxWeekdayBooking = '';
      }


      if (that.facility.maxWeekendBooking == null || that.facility.maxWeekendBooking === undefined) {
        that.facility.maxWeekendBooking = '';
      }


      if (that.facility.maxBookingHoursWeekday == null || that.facility.maxBookingHoursWeekday === undefined) {
        that.facility.maxBookingHoursWeekday = '';
      }


      if (that.facility.maxBookingHoursWeekend == null || that.facility.maxBookingHoursWeekend === undefined) {
        that.facility.maxBookingHoursWeekend = '';
      }


      if (that.facility.slotDuration == null || that.facility.slotDuration === undefined || that.facility.slotDuration + '' === '0') {
        that.facility.slotDuration = '';
      }


      if (that.facility.maxBookingsPerSlot == null || that.facility.maxBookingsPerSlot === undefined) {
        that.facility.maxBookingsPerSlot = '';
      }


      if (that.facility.GSTValue == null || that.facility.GSTValue === undefined) {
        that.facility.GSTValue = '';
      }


      // Event-specific fields
      if (that.facility.eventType == null || that.facility.eventType === undefined) {
        that.facility.eventType = '';
      }


      if (that.facility.date == null || that.facility.date === undefined) {
        that.facility.date = '';
      }


      if (that.facility.location == null || that.facility.location === undefined) {
        that.facility.location = '';
      }


      // Initialize 5-tier pricing structure
      if (!that.facility.activityCharges) {
        that.facility.activityCharges = {
          member: { enabled: false, amount: '' },
          nonMember: { enabled: false, amount: '' },
          guest: { enabled: false, amount: '' },
          corpMember: { enabled: false, amount: '' },
          corpNonMember: { enabled: false, amount: '' }
        };
      }


      // Event-specific: Always set category to Events
      that.facility.category = 'Events';


      // Event-specific: Always disable peak and break times
      that.facility.isPeakTimeEnabled = false;
      that.facility.isBreakTimeEnabled = false;
      that.facility.isSlotsLocked = true;


    } catch (err) {
      console.log(err);
    }
  }


  /* ================================================================
     View Preparation Methods
  ===================================================================*/


  /**
   * Set flags before viewing event
   * Initialize component state for viewing events
   */
  setFlagBeforeViewEvent(that: any): void {
    that.viewIcon.name = '';
    that.viewIcon.iconClass = '';


    // Event-specific: Clear peak and break timings (not used for events)
    that.facility.peekTimings = [];
    that.peekTimingsArray = [];
    that.closeTimeArray = [];


    that.defaultValue = true;
    that.saveFlag = true;
    that.functionHallFlag = false;
    that.viewStatus = true;
    that.isReadOnly = true;
    that.editStatus = false;
    that.addEventStatus = false;


    // Event-specific: Ensure proper category and disabled features
    that.facility.category = 'Events';
    that.facility.isPeakTimeEnabled = false;
    that.facility.isBreakTimeEnabled = false;
    that.facility.isSlotsLocked = true;
  }


  /**
   * Set timings for event viewing
   * Parse and set timing values for display
   */
  setTimingsForEvent(that: any): void {
    let timeObject = {};


    // Parse open and close times
    if (that.facility.timings.openTime) {
      let openTime = that.facility.timings.openTime.split(':');
      that.openTime_hour = ('0' + parseInt(openTime[0])).slice(-2);
      that.openTime_minute = ('0' + parseInt(openTime[1])).slice(-2);
    }


    if (that.facility.timings.closeTime) {
      let closeTime = that.facility.timings.closeTime.split(':');
      that.closeTime_hour = ('0' + parseInt(closeTime[0])).slice(-2);
      that.closeTime_minute = ('0' + parseInt(closeTime[1])).slice(-2);
    }


    // Legacy: Parse break timings (not used for events but kept for compatibility)
    for (let i = 0; i < that.facility.breakTimings.length; i++) {
      timeObject['start'] = that.facility.breakTimings[i].start.replace(/:00:00/g, '');
      timeObject['end'] = that.facility.breakTimings[i].end.replace(/:00:00/g, '');
      that.closeTimeArray.push(timeObject);
      timeObject = {};
    }


    // Legacy: Parse peak timings (not used for events but kept for compatibility)
    for (let item of that.facility.peekTimings) {
      timeObject['start'] = item.start.replace(/:00:00/g, '');
      timeObject['end'] = item.end.replace(/:00:00/g, '');
      that.peekTimingsArray.push(timeObject);
      timeObject = {};
    }
  }


  /* ================================================================
     Event-Specific Helper Methods
  ===================================================================*/


  /**
   * Check if any pricing category is enabled (for GST validation)
   * Event-specific method for 5-tier pricing system
   */
  protected hasAnyPricingEnabled(that: any): boolean {
    if (!that.facility.activityCharges) {
      return false;
    }


    const categories = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      if (that.facility.activityCharges[category] &&
          that.facility.activityCharges[category].enabled) {
        return true;
      }
    }
    return false;
  }


  /**
   * Enforce event-specific business rules
   * Events have different rules than regular facilities
   */
  protected enforceEventRules(that: any): void {
    // Always disable peak and break times for events
    that.facility.isPeakTimeEnabled = false;
    that.facility.isBreakTimeEnabled = false;
    that.isPeakTimeEnabled = false;
    that.isBreakTimeEnabled = false;


    // Always lock slots for events
    that.facility.isSlotsLocked = true;
    that.isSlotsLocked = true;


    // Clear peak and break timing arrays
    that.facility.peekTimings = [];
    that.facility.breakTimings = [];
    that.peekTimingsArray = [];
    that.closeTimeArray = [];


    // Set fixed category
    that.facility.category = 'Events';


    // Clear peak units (not applicable to events)
    that.facility.peakUnitsCount = '';
  }


  /**
   * Initialize default event structure
   * Set up default values for new events
   */
  protected initializeEventDefaults(that: any): void {
    // Initialize 5-tier pricing structure
    if (!that.facility.activityCharges) {
      that.facility.activityCharges = {
        member: { enabled: false, amount: '' },
        nonMember: { enabled: false, amount: '' },
        guest: { enabled: false, amount: '' },
        corpMember: { enabled: false, amount: '' },
        corpNonMember: { enabled: false, amount: '' }
      };
    }


    // Set default waitlist count
    if (that.isWaitlistEnabled && (!that.facility.waitlistCount || that.facility.waitlistCount === '')) {
      that.facility.waitlistCount = '3';
    }


    // Initialize event-specific fields
    that.facility.eventType = that.facility.eventType || '';
    that.facility.date = that.facility.date || '';
    that.facility.location = that.facility.location || '';
    that.facility.maxNumberOfPeople = that.facility.maxNumberOfPeople || '';


    // Enforce event rules
    this.enforceEventRules(that);
  }


  /* ================================================================
     Legacy Methods (kept for backward compatibility)
  ===================================================================*/


  setLocalFlagWithFacilityRules(that: any): void {
    this.setLocalFlagWithEventRules(that);
  }


  setFlagBeforeViewFacility(that: any): void {
    this.setFlagBeforeViewEvent(that);
  }


  setTimingsForFacility(that: any): void {
    this.setTimingsForEvent(that);
  }


  setFlagsForDisableBookings(that: any): void {
    that.functionHallFlag = true;
    that.facility.slotDuration = '';
    that.facility.peakUnitsCount = '';
    that.peekTimingsArray = [];
    that.isPeakTimeEnabled = false;
  }


  removeCloseTime(that: any, time: any): void {
    try {
      let index = that.closeTimeArray.indexOf(time);
      that.closeTimeArray.splice(index, 1);
    } catch (err) {
      console.log(err);
    }
  }


  addCloseTime(that: any): void {
    try {
      that.closedStartTime = that.closedStartTime_hour && that.closedStartTime_minute
        ? that.closedStartTime_hour + ':' + that.closedStartTime_minute : '';
      that.closedEndTime = that.closedEndTime_hour && that.closedEndTime_minute
        ? that.closedEndTime_hour + ':' + that.closedEndTime_minute : '';


      if (that.closedStartTime && that.closedEndTime) {
        that.closedTime['start'] = that.closedStartTime;
        that.closedTime['end'] = that.closedEndTime;
        that.closeTimeArray.push(that.closedTime);
        that.closedTime = {};


        // Reset values after adding
        that.closedStartTime_hour = '';
        that.closedStartTime_minute = '';
        that.closedStartTime = '';
        that.closedEndTime = '';
        that.closedEndTime_hour = '';
        that.closedEndTime_minute = '';
      }
    } catch (err) {
      console.log(err);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/facility/event/event-base.ts